import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Modal, Select } from '../components/forms';
/**
 * TEAM MEMBERS PAGE
 *
 * Manage organization team members and invitations.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Banner,
  Badge,
  DataTable,
  EmptyState,
  Spinner,
} from '@shopify/polaris';
import { useAuth } from '../App';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

interface Member {
  id: number;
  userId: number;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'suspended';
  joinedAt: string | null;
  picture?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
}

export default function TeamMembers() {
  const navigate = useNavigate();
  const { currentOrganization, user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  // Invite modal
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [editRole, setEditRole] = useState('member');
  const [isUpdating, setIsUpdating] = useState(false);

  // Current user's role
  const currentUserRole = members.find(m => m.userId === Number(user?.id))?.role;
  const canManageTeam = currentUserRole === 'owner' || currentUserRole === 'admin';

  useEffect(() => {
    if (currentOrganization) {
      loadMembers();
    }
  }, [currentOrganization]);

  const loadMembers = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const [membersRes, invitationsRes] = await Promise.all([
        fetch(`${API_URL}/api/organizations/${currentOrganization.id}/members`, {
          credentials: 'include',
        }),
        fetch(`${API_URL}/api/organizations/${currentOrganization.id}/invitations`, {
          credentials: 'include',
        }),
      ]);

      const membersData = await membersRes.json();
      const invitationsData = await invitationsRes.json();

      if (membersData.success) {
        setMembers(membersData.members || []);
      }
      if (invitationsData.success) {
        setInvitations(invitationsData.invitations || []);
      }
    } catch {
      setError('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = useCallback(async () => {
    if (!currentOrganization || !inviteEmail.trim()) return;

    setIsInviting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/organizations/${currentOrganization.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to send invitation');
        return;
      }

      setSuccess('Invitation sent successfully');
      setInviteModalOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      loadMembers();
    } catch {
      setError('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  }, [currentOrganization, inviteEmail, inviteRole]);

  const handleUpdateRole = useCallback(async () => {
    if (!currentOrganization || !editingMember) return;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${currentOrganization.id}/members/${editingMember.userId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ role: editRole }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to update member');
        return;
      }

      setSuccess('Member updated successfully');
      setEditModalOpen(false);
      setEditingMember(null);
      loadMembers();
    } catch {
      setError('Failed to update member');
    } finally {
      setIsUpdating(false);
    }
  }, [currentOrganization, editingMember, editRole]);

  const handleRemoveMember = useCallback(async (memberId: number) => {
    if (!currentOrganization) return;
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${currentOrganization.id}/members/${memberId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to remove member');
        return;
      }

      setSuccess('Member removed successfully');
      loadMembers();
    } catch {
      setError('Failed to remove member');
    }
  }, [currentOrganization]);

  const handleCancelInvitation = useCallback(async (invitationId: string) => {
    if (!currentOrganization) return;
    if (!confirm('Are you sure you want to cancel this invitation?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/organizations/${currentOrganization.id}/invitations/${invitationId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to cancel invitation');
        return;
      }

      setSuccess('Invitation cancelled');
      loadMembers();
    } catch {
      setError('Failed to cancel invitation');
    }
  }, [currentOrganization]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return <Badge tone="success">Owner</Badge>;
      case 'admin':
        return <Badge tone="info">Admin</Badge>;
      default:
        return <Badge>Member</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'invited':
        return <Badge tone="attention">Invited</Badge>;
      case 'suspended':
        return <Badge tone="critical">Suspended</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (!currentOrganization) {
    return (
      <Page title="Team Members">
        <Banner tone="warning">
          Please select an organization to manage team members.
        </Banner>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page title="Team Members">
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  const memberRows = members.map((member) => [
    <InlineStack gap="200" blockAlign="center" key={member.id}>
      {member.picture ? (
        <img
          src={member.picture}
          alt={member.name}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#4a9b8c',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {member.name?.charAt(0).toUpperCase() || 'U'}
        </div>
      )}
      <BlockStack gap="100">
        <Text as="span" variant="bodyMd" fontWeight="semibold">{member.name}</Text>
        <Text as="span" variant="bodySm" tone="subdued">{member.email}</Text>
      </BlockStack>
    </InlineStack>,
    getRoleBadge(member.role),
    getStatusBadge(member.status),
    member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : '-',
    canManageTeam && member.role !== 'owner' && member.userId !== Number(user?.id) ? (
      <InlineStack gap="200">
        <Button
          size="slim"
          onClick={() => {
            setEditingMember(member);
            setEditRole(member.role);
            setEditModalOpen(true);
          }}
        >
          Edit
        </Button>
        <Button
          size="slim"
          tone="critical"
          onClick={() => handleRemoveMember(member.userId)}
        >
          Remove
        </Button>
      </InlineStack>
    ) : null,
  ]);

  const invitationRows = invitations.map((inv) => [
    inv.email,
    getRoleBadge(inv.role),
    <Badge tone="attention" key={inv.id}>Pending</Badge>,
    `Expires ${new Date(inv.expiresAt).toLocaleDateString()}`,
    canManageTeam ? (
      <Button
        size="slim"
        tone="critical"
        onClick={() => handleCancelInvitation(inv.id)}
      >
        Cancel
      </Button>
    ) : null,
  ]);

  return (
    <Page
      title="Team Members"
      subtitle={`${currentOrganization.name}`}
      backAction={{ content: 'Organization Settings', onAction: () => navigate('/settings/organization') }}
      primaryAction={
        canManageTeam
          ? {
              icon: PlusIcon, content: 'Invite Member',
              onAction: () => setInviteModalOpen(true),
            }
          : undefined
      }
    >
      <BlockStack gap="600">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {success && (
          <Banner tone="success" onDismiss={() => setSuccess(null)}>
            {success}
          </Banner>
        )}

        {/* Active Members */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Active Members ({members.length})
            </Text>

            {members.length === 0 ? (
              <EmptyState
                heading="No team members"
                image=""
              >
                <p>Invite team members to collaborate on your organization.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                headings={['Member', 'Role', 'Status', 'Joined', 'Actions']}
                rows={memberRows}
              />
            )}
          </BlockStack>
        </Card>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Pending Invitations ({invitations.length})
              </Text>

              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                headings={['Email', 'Role', 'Status', 'Expires', 'Actions']}
                rows={invitationRows}
              />
            </BlockStack>
          </Card>
        )}
      </BlockStack>

      {/* Invite Modal */}
      <Modal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        title="Invite Team Member"
        primaryAction={{
          content: 'Send Invitation',
          onAction: handleInvite,
          loading: isInviting,
          disabled: !inviteEmail.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setInviteModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Email Address"
              type="email"
              requiredIndicator value={inviteEmail}
              onChange={setInviteEmail}
              autoComplete="email"
              autoFocus
            />
            <Select
              label="Role"
              options={[
                { label: 'Member', value: 'member' },
                { label: 'Admin', value: 'admin' },
              ]}
              requiredIndicator value={inviteRole}
              onChange={setInviteRole}
              helpText="Admins can manage team members and settings. Members have standard access."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Edit ${editingMember?.name}'s Role`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleUpdateRole,
          loading: isUpdating,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Select
            label="Role"
            options={[
              { label: 'Member', value: 'member' },
              { label: 'Admin', value: 'admin' },
            ]}
            requiredIndicator value={editRole}
            onChange={setEditRole}
            helpText="Admins can manage team members and settings. Members have standard access."
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}
